<template>
  <AdminLayout page-title="Advanced Analytics" page-subtitle="Real-time insights and analytics for your oilfield operations">
  <div class="space-y-8">
      <!-- Page Header -->
      <div class="mb-8">
        <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
          <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
          Advanced Analytics
        </div>
        <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
          Performance Dashboard
        </h1>
        <p class="text-xl text-slate-300 mb-8 max-w-2xl">
          Real-time insights and analytics for your oilfield operations.
        </p>
      </div>

      <p v-if="loading" class="text-slate-400 mb-4">Loading live platform analytics…</p>
      <p v-if="loadError" class="text-red-400 mb-4">{{ loadError }}</p>

      <!-- Analytics Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="analytics-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Revenue</p>
              <p class="text-3xl font-bold text-white">${{ formatCurrency(analytics.totalRevenue) }}</p>
              <p class="text-sm mt-2" :class="analytics.revenueGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'">{{ analytics.revenueGrowth >= 0 ? '+' : '' }}{{ analytics.revenueGrowth }}% vs last period</p>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="analytics-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Active Jobs</p>
              <p class="text-3xl font-bold text-white">{{ analytics.activeJobs }}</p>
              <p class="text-sm text-emerald-400 mt-2">{{ analytics.completionRate }}% completion rate</p>
            </div>
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="analytics-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Avg Cost per Well</p>
              <p class="text-3xl font-bold text-white">${{ formatCurrency(analytics.avgCostPerWell) }}</p>
              <p class="text-sm mt-2" :class="analytics.costReduction >= 0 ? 'text-emerald-400' : 'text-red-400'">{{ analytics.costReduction >= 0 ? '-' : '+' }}{{ Math.abs(analytics.costReduction) }}% vs last period</p>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="analytics-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Crew Utilization</p>
              <p class="text-3xl font-bold text-white">{{ analytics.crewUtilization }}%</p>
              <p class="text-sm text-slate-400 mt-2">Field accounts with activity this period</p>
            </div>
            <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Cost Analysis per Well -->
      <div class="analytics-card">
        <div class="p-6 border-b border-slate-700/50 flex items-center justify-between">
          <div>
            <h3 class="text-xl font-bold text-white mb-2">Cost Analysis per Well</h3>
            <p class="text-slate-400">Detailed breakdown of operational costs</p>
          </div>
          <div class="flex items-center space-x-3">
            <select v-model="costTimeframe" class="analytics-select">
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <button @click="exportCostData" class="btn-analytics">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export
            </button>
          </div>
        </div>
        <div class="p-6">
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead>
                <tr class="border-b border-slate-700/50">
                  <th class="analytics-table-header text-left">Well Name</th>
                  <th class="analytics-table-header text-left">Job Type</th>
                  <th class="analytics-table-header text-left">Duration</th>
                  <th class="analytics-table-header text-left">Labor Cost</th>
                  <th class="analytics-table-header text-left">Equipment Cost</th>
                  <th class="analytics-table-header text-left">Total Cost</th>
                  <th class="analytics-table-header text-left">Cost per Hour</th>
                  <th class="analytics-table-header text-left">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/30">
                <tr v-if="!costAnalysis.length">
                  <td colspan="8" class="analytics-table-cell text-slate-400 text-center">No field tickets in this period.</td>
                </tr>
                <tr v-for="well in costAnalysis" :key="well.id" class="analytics-table-row">
                  <td class="analytics-table-cell">
                    <div class="font-medium text-white">{{ well.name }}</div>
                    <div class="text-sm text-slate-400">{{ well.location }}</div>
                  </td>
                <td class="table-cell">
                  <span :class="getJobTypeBadgeClass(well.jobType)" class="badge">
                    {{ well.jobType }}
                  </span>
                </td>
                <td class="table-cell">
                  <div class="text-sm text-oil-900">{{ well.duration }} days</div>
                  <div class="text-xs text-oil-600">{{ well.hours }} hours</div>
                </td>
                <td class="table-cell">
                  <div class="text-sm font-medium text-oil-900">${{ formatCurrency(well.laborCost) }}</div>
                  <div class="text-xs text-oil-600">{{ well.crewSize }} crew</div>
                </td>
                <td class="table-cell">
                  <div class="text-sm font-medium text-oil-900">${{ formatCurrency(well.equipmentCost) }}</div>
                  <div class="text-xs text-oil-600">{{ well.equipmentCount }} items</div>
                </td>
                <td class="table-cell">
                  <div class="text-lg font-bold text-oil-900">${{ formatCurrency(well.totalCost) }}</div>
                </td>
                <td class="table-cell">
                  <div class="text-sm text-oil-900">${{ formatCurrency(well.costPerHour) }}/hr</div>
                  <div class="text-xs text-oil-600" :class="getCostEfficiencyClass(well.costPerHour)">
                    {{ getCostEfficiency(well.costPerHour) }}
                  </div>
                </td>
                <td class="table-cell">
                  <span :class="getStatusBadgeClass(well.status)" class="badge">
                    {{ well.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cost Summary -->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-oil-50 rounded-lg">
          <div class="text-center">
            <div class="text-2xl font-bold text-oil-900">${{ formatCurrency(costSummary.totalCost) }}</div>
            <div class="text-sm text-oil-600">Total Cost</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-primary-600">${{ formatCurrency(costSummary.avgCostPerWell) }}</div>
            <div class="text-sm text-oil-600">Avg per Well</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-success-600">${{ formatCurrency(costSummary.avgCostPerHour) }}</div>
            <div class="text-sm text-oil-600">Avg per Hour</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-warning-600">{{ costSummary.costEfficiency }}%</div>
            <div class="text-sm text-oil-600">Cost Efficiency</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contractor Utilization Metrics -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-semibold text-oil-900">Contractor Utilization Metrics</h3>
        <div class="flex items-center space-x-2">
          <select v-model="utilizationTimeframe" class="form-select text-sm">
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
          </select>
        </div>
      </div>
      <div class="card-body">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Utilization Chart -->
          <div>
            <h4 class="font-medium text-oil-900 mb-4">Utilization by Role</h4>
            <p v-if="!utilizationByDepartment.length" class="text-sm text-slate-400">No field accounts yet.</p>
            <div v-else class="space-y-4">
              <div v-for="dept in utilizationByDepartment" :key="dept.name" class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-oil-700">{{ dept.name }}</span>
                  <span class="text-sm text-oil-600">{{ dept.utilization }}%</span>
                </div>
                <div class="w-full bg-oil-200 rounded-full h-2">
                  <div :class="getUtilizationBarClass(dept.utilization)" 
                       class="h-2 rounded-full transition-all duration-300" 
                       :style="{ width: dept.utilization + '%' }"></div>
                </div>
                <div class="flex justify-between text-xs text-oil-500">
                  <span>{{ dept.activeMembers }}/{{ dept.totalMembers }} active</span>
                  <span>{{ dept.avgHours }}h avg</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Performers -->
          <div>
            <h4 class="font-medium text-oil-900 mb-4">Top Performers</h4>
            <p v-if="!topPerformers.length" class="text-sm text-slate-400">No hours logged in this period.</p>
            <div v-else class="space-y-3">
              <div v-for="(contractor, index) in topPerformers" :key="contractor.id" 
                   class="flex items-center justify-between p-3 bg-oil-50 rounded-lg">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                       :class="index === 0 ? 'bg-yellow-500 text-white' : 
                               index === 1 ? 'bg-oil-500 text-white' : 
                               'bg-oil-200 text-oil-700'">
                    {{ index + 1 }}
                  </div>
                  <div>
                    <div class="font-medium text-oil-900">{{ contractor.name }}</div>
                    <div class="text-sm text-oil-600">{{ contractor.position }}</div>
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-lg font-bold text-oil-900">{{ contractor.utilization }}%</div>
                  <div class="text-sm text-oil-600">{{ contractor.hours }}h worked</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Safety Performance Tracking -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-semibold text-oil-900">Safety Performance Tracking</h3>
        <div class="flex items-center space-x-2">
          <select v-model="safetyTimeframe" class="form-select text-sm">
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>
      <div class="card-body">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <!-- TRIR Trend -->
          <div class="text-center">
            <h4 class="font-medium text-oil-900 mb-2">TRIR Trend</h4>
            <div class="text-3xl font-bold text-primary-600 mb-2">{{ safetyMetrics.trir }}</div>
            <div class="text-sm text-oil-600">per 200,000 hours</div>
            <div class="mt-2">
              <span :class="safetyMetrics.trirTrend === 'down' ? 'text-success-600' : 'text-danger-600'" class="text-sm">
                {{ safetyMetrics.trirTrend === 'down' ? '↓' : '↑' }} {{ safetyMetrics.trirChange }}%
              </span>
              <span class="text-xs text-oil-500 ml-1">vs last period</span>
            </div>
          </div>

          <!-- Safety Score -->
          <div class="text-center">
            <h4 class="font-medium text-oil-900 mb-2">Safety Score</h4>
            <div class="text-3xl font-bold text-success-600 mb-2">{{ safetyMetrics.safetyScore }}/100</div>
            <div class="text-sm text-oil-600">overall rating</div>
            <div class="mt-2">
              <span :class="safetyMetrics.safetyTrend === 'up' ? 'text-success-600' : 'text-danger-600'" class="text-sm">
                {{ safetyMetrics.safetyTrend === 'up' ? '↑' : '↓' }} {{ safetyMetrics.safetyChange }}%
              </span>
              <span class="text-xs text-oil-500 ml-1">vs last period</span>
            </div>
          </div>

          <!-- Training Completion -->
          <div class="text-center">
            <h4 class="font-medium text-oil-900 mb-2">Training Completion</h4>
            <div class="text-3xl font-bold text-warning-600 mb-2">{{ safetyMetrics.trainingCompletion }}%</div>
            <div class="text-sm text-oil-600">crew members</div>
            <div class="mt-2">
              <span :class="safetyMetrics.trainingTrend === 'up' ? 'text-success-600' : 'text-danger-600'" class="text-sm">
                {{ safetyMetrics.trainingTrend === 'up' ? '↑' : '↓' }} {{ safetyMetrics.trainingChange }}%
              </span>
              <span class="text-xs text-oil-500 ml-1">vs last period</span>
            </div>
          </div>
        </div>

        <!-- Safety Chart -->
        <div class="h-64 bg-oil-50 rounded-lg flex items-center justify-center">
          <div class="text-center">
            <svg class="w-16 h-16 text-oil-300 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 14H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <p class="text-oil-600">{{ safetyMetrics.trir }} TRIR from live incidents and worked hours.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Performance Benchmarking -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-semibold text-oil-900">Performance Benchmarking</h3>
        <div class="flex items-center space-x-2">
          <select v-model="benchmarkCategory" class="form-select text-sm">
            <option value="cost">Cost Efficiency</option>
            <option value="safety">Safety Performance</option>
            <option value="productivity">Productivity</option>
            <option value="quality">Work Quality</option>
          </select>
        </div>
      </div>
      <div class="card-body">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Benchmark Comparison -->
          <div>
            <h4 class="font-medium text-oil-900 mb-4">Platform Comparison</h4>
            <div class="space-y-4">
              <div v-for="metric in benchmarkMetrics" :key="metric.name" class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-oil-700">{{ metric.name }}</span>
                  <span class="text-sm text-oil-600">{{ metric.value }}</span>
                </div>
                <div class="w-full bg-oil-200 rounded-full h-2">
                  <div :class="getBenchmarkBarClass(metric.percentile)" 
                       class="h-2 rounded-full transition-all duration-300" 
                       :style="{ width: metric.percentile + '%' }"></div>
                </div>
                <div class="flex justify-between text-xs text-oil-500">
                  <span>{{ metric.industryAvg }}</span>
                  <span :class="getBenchmarkPercentileClass(metric.percentile)">
                    {{ getBenchmarkPercentile(metric.percentile) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Improvement Areas -->
          <div>
            <h4 class="font-medium text-oil-900 mb-4">Areas for Improvement</h4>
            <p v-if="!improvementAreas.length" class="text-sm text-slate-400">No issues flagged from current records.</p>
            <div v-else class="space-y-3">
              <div v-for="area in improvementAreas" :key="area.id" 
                   class="border border-oil-200 rounded-lg p-4">
                <div class="flex items-start space-x-3">
                  <div :class="getImprovementIconClass(area.priority)" class="w-6 h-6 mt-0.5">
                    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <h5 class="font-medium text-oil-900">{{ area.title }}</h5>
                    <p class="text-sm text-oil-600">{{ area.description }}</p>
                    <div class="mt-2 flex items-center space-x-2">
                      <span :class="getPriorityBadgeClass(area.priority)" class="badge">
                        {{ area.priority }}
                      </span>
                      <span class="text-xs text-oil-500">{{ area.impact }} impact</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useAdminStore } from '../../stores/adminStore'
import AdminLayout from '../admin/AdminLayout.vue'

const adminStore = useAdminStore()

// State
const costTimeframe = ref('month')
const utilizationTimeframe = ref('month')
const safetyTimeframe = ref('month')
const benchmarkCategory = ref('cost')

const analytics = ref({
  totalRevenue: 0,
  revenueGrowth: 0,
  activeJobs: 0,
  completionRate: 0,
  avgCostPerWell: 0,
  costReduction: 0,
  crewUtilization: 0,
  utilizationGrowth: 0
})

const costAnalysis = ref<any[]>([])

const costSummary = computed(() => {
  if (!costAnalysis.value.length) {
    return { totalCost: 0, avgCostPerWell: 0, avgCostPerHour: 0, costEfficiency: 0 }
  }
  const totalCost = costAnalysis.value.reduce((sum, well) => sum + Number(well.totalCost || 0), 0)
  const totalHours = costAnalysis.value.reduce((sum, well) => sum + Number(well.hours || 0), 0)
  const wells = new Set(costAnalysis.value.map((well) => well.name)).size || 1
  const avgCostPerWell = totalCost / wells
  const avgCostPerHour = totalHours > 0 ? totalCost / totalHours : 0
  const costEfficiency = avgCostPerHour > 0 ? Math.max(0, Math.min(100, Math.round(100 - Math.min(avgCostPerHour, 100)))) : 0
  return {
    totalCost: Math.round(totalCost),
    avgCostPerWell: Math.round(avgCostPerWell),
    avgCostPerHour: Math.round(avgCostPerHour),
    costEfficiency
  }
})

const utilizationByDepartment = ref<any[]>([])
const topPerformers = ref<any[]>([])
const safetyMetrics = ref({
  trir: 0,
  trirTrend: 'down',
  trirChange: 0,
  safetyScore: 0,
  safetyTrend: 'up',
  safetyChange: 0,
  trainingCompletion: 0,
  trainingTrend: 'up',
  trainingChange: 0
})
const benchmarkMetrics = ref<any[]>([])
const improvementAreas = ref<any[]>([])
const loading = ref(false)
const loadError = ref('')

// Methods
const getJobTypeBadgeClass = (jobType: string) => {
  switch (jobType) {
    case 'Flowback':
      return 'badge-primary'
    case 'Well Testing':
      return 'badge-success'
    case 'Production':
      return 'badge-warning'
    default:
      return 'badge-oil'
  }
}

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'badge-success'
    case 'in_progress':
      return 'badge-primary'
    case 'scheduled':
      return 'badge-warning'
    default:
      return 'badge-oil'
  }
}

const getCostEfficiencyClass = (costPerHour: number) => {
  if (costPerHour <= 180) return 'text-success-600'
  if (costPerHour <= 200) return 'text-warning-600'
  return 'text-danger-600'
}

const getCostEfficiency = (costPerHour: number) => {
  if (costPerHour <= 180) return 'Excellent'
  if (costPerHour <= 200) return 'Good'
  if (costPerHour <= 220) return 'Average'
  return 'Below Average'
}

const getUtilizationBarClass = (utilization: number) => {
  if (utilization >= 90) return 'bg-success-600'
  if (utilization >= 80) return 'bg-warning-600'
  if (utilization >= 70) return 'bg-oil-600'
  return 'bg-danger-600'
}

const getBenchmarkBarClass = (percentile: number) => {
  if (percentile >= 90) return 'bg-success-600'
  if (percentile >= 75) return 'bg-primary-600'
  if (percentile >= 50) return 'bg-warning-600'
  return 'bg-danger-600'
}

const getBenchmarkPercentileClass = (percentile: number) => {
  if (percentile >= 90) return 'text-success-600'
  if (percentile >= 75) return 'text-primary-600'
  if (percentile >= 50) return 'text-warning-600'
  return 'text-danger-600'
}

const getBenchmarkPercentile = (percentile: number) => {
  if (percentile >= 90) return 'Top 10%'
  if (percentile >= 75) return 'Top 25%'
  if (percentile >= 50) return 'Top 50%'
  return 'Below Average'
}

const getImprovementIconClass = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'text-danger-500'
    case 'medium':
      return 'text-warning-500'
    case 'low':
      return 'text-oil-500'
    default:
      return 'text-oil-500'
  }
}

const getPriorityBadgeClass = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'badge-danger'
    case 'medium':
      return 'badge-warning'
    case 'low':
      return 'badge-oil'
    default:
      return 'badge-oil'
  }
}

const exportCostData = async () => {
  try {
    const params = new URLSearchParams({
      cost: costTimeframe.value,
      utilization: utilizationTimeframe.value,
      safety: safetyTimeframe.value
    })
    const response = await fetch(`/api/admin/analytics/export?${params}`, {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `analytics-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  } catch (error) {
    console.error('Error exporting analytics data:', error)
  }
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US').format(amount)
}

const loadAnalyticsData = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const params = new URLSearchParams({
      cost: costTimeframe.value,
      utilization: utilizationTimeframe.value,
      safety: safetyTimeframe.value
    })
    const response = await fetch(`/api/admin/analytics?${params}`, {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.error || `Analytics failed (${response.status})`)
    }
    analytics.value = {
      totalRevenue: Number(data.analytics?.totalRevenue || 0),
      revenueGrowth: Number(data.analytics?.revenueGrowth || 0),
      activeJobs: Number(data.analytics?.activeJobs || 0),
      completionRate: Number(data.analytics?.completionRate || 0),
      avgCostPerWell: Number(data.analytics?.avgCostPerWell || 0),
      costReduction: Number(data.analytics?.costReduction || 0),
      crewUtilization: Number(data.analytics?.crewUtilization || 0),
      utilizationGrowth: Number(data.analytics?.utilizationGrowth || 0)
    }
    costAnalysis.value = Array.isArray(data.costAnalysis) ? data.costAnalysis : []
    utilizationByDepartment.value = Array.isArray(data.utilizationByDepartment) ? data.utilizationByDepartment : []
    topPerformers.value = Array.isArray(data.topPerformers) ? data.topPerformers : []
    if (data.safetyMetrics) safetyMetrics.value = { ...safetyMetrics.value, ...data.safetyMetrics }
    benchmarkMetrics.value = Array.isArray(data.benchmarkMetrics) ? data.benchmarkMetrics : []
    improvementAreas.value = Array.isArray(data.improvementAreas) ? data.improvementAreas : []
  } catch (error) {
    console.error('Error loading analytics data:', error)
    loadError.value = error instanceof Error ? error.message : 'Could not load analytics.'
    analytics.value = { totalRevenue: 0, revenueGrowth: 0, activeJobs: 0, completionRate: 0, avgCostPerWell: 0, costReduction: 0, crewUtilization: 0, utilizationGrowth: 0 }
    costAnalysis.value = []
    utilizationByDepartment.value = []
    topPerformers.value = []
    benchmarkMetrics.value = []
    improvementAreas.value = []
  } finally {
    loading.value = false
  }
}

watch([costTimeframe, utilizationTimeframe, safetyTimeframe], () => {
  loadAnalyticsData()
})

onMounted(() => {
  loadAnalyticsData()
})
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Analytics Components */
.analytics-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.analytics-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.analytics-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50;
}

.btn-analytics {
  @apply inline-flex items-center px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 font-medium rounded-lg transition-colors duration-200 border border-emerald-500/30 hover:border-emerald-500/50;
}

.analytics-table-header {
  @apply px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider;
}

.analytics-table-row {
  @apply hover:bg-slate-700/20 transition-colors duration-150;
}

.analytics-table-cell {
  @apply px-6 py-4 whitespace-nowrap;
}

/* Badge styles */
.badge {
  @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium;
}

.badge-primary {
  @apply bg-emerald-500/20 text-emerald-400;
}

.badge-success {
  @apply bg-emerald-500/20 text-emerald-400;
}

.badge-warning {
  @apply bg-yellow-500/20 text-yellow-400;
}

.badge-danger {
  @apply bg-red-500/20 text-red-400;
}

.badge-oil {
  @apply bg-slate-500/20 text-slate-400;
}

/* Utility bar classes */
.bg-success-600 {
  @apply bg-emerald-500;
}

.bg-warning-600 {
  @apply bg-yellow-500;
}

.bg-oil-600 {
  @apply bg-slate-500;
}

.bg-danger-600 {
  @apply bg-red-500;
}

.bg-primary-600 {
  @apply bg-emerald-500;
}

/* Text color classes */
.text-success-600 {
  @apply text-emerald-400;
}

.text-warning-600 {
  @apply text-yellow-400;
}

.text-danger-600 {
  @apply text-red-400;
}

.text-primary-600 {
  @apply text-emerald-400;
}

.text-oil-900 {
  @apply text-white;
}

.text-oil-600 {
  @apply text-slate-300;
}

.text-oil-500 {
  @apply text-slate-400;
}

.text-oil-400 {
  @apply text-slate-500;
}

.text-oil-300 {
  @apply text-slate-600;
}

.text-danger-500 {
  @apply text-red-400;
}

.text-warning-500 {
  @apply text-yellow-400;
}

.text-oil-500 {
  @apply text-slate-400;
}

/* Additional overrides for complete theme consistency */
.bg-oil-50 {
  @apply bg-slate-800/30;
}

.bg-oil-100 {
  @apply bg-slate-700/50;
}

.bg-oil-200 {
  @apply bg-slate-600/50;
}

.border-oil-200 {
  @apply border-slate-600/50;
}

.text-oil-700 {
  @apply text-slate-300;
}

.card {
  @apply bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-emerald-500/20 transition-all duration-300;
}

.card-header {
  @apply px-6 py-4 border-b border-slate-700/50 bg-slate-800/20;
}

.card-body {
  @apply p-6;
}

.table-cell {
  @apply px-6 py-4 whitespace-nowrap text-sm;
}

/* Table styling */
.table-header {
  @apply px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider bg-slate-800/50;
}

/* Chart placeholder styling */
.chart-placeholder {
  @apply bg-slate-800/30 border border-slate-700/50;
}

/* Form select styling - override default white selects */
.form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50;
}

.form-select option {
  @apply bg-slate-800 text-slate-200;
}

/* Ensure all select elements follow dark theme */
select {
  @apply bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50;
}

select option {
  @apply bg-slate-800 text-slate-200;
}
</style>
